import { safe } from "../../../libs/utils";
import { parseCompoundCondition } from "./ConditionParser";
import { CartEvaluator, ElapsedTimeEvaluator, ProductEvaluator } from "./evaluators";
import { ConditionEvaluatorInterface, ConditionType, ParsedCondition } from "./types";

export class ConditionEvaluator {
  private evaluators: Map<ConditionType, ConditionEvaluatorInterface> = new Map();
  private disposers: Set<() => void> = new Set();

  constructor() {
    this.registerEvaluator(new ElapsedTimeEvaluator());
    this.registerEvaluator(new CartEvaluator());
    this.registerEvaluator(new ProductEvaluator());
  }

  private registerEvaluator(evaluator: ConditionEvaluatorInterface): void {
    this.evaluators.set(evaluator.type, evaluator);
  }

  watchCondition(query: string, callback: () => void): () => void {
    const compound = parseCompoundCondition(query);
    if (!compound) {
      console.warn(`Invalid condition query: ${query}`);
      return () => {};
    }

    if (compound.operator === null) {
      return this._watchSingle(safe(compound.conditions[0]), callback);
    }

    if (compound.operator === "AND") {
      return this._watchAnd(compound.conditions, callback);
    }

    return this._watchOr(compound.conditions, callback);
  }

  private _watchSingle(condition: ParsedCondition, callback: () => void): () => void {
    const evaluator = this.evaluators.get(condition.type);
    if (!evaluator) {
      console.warn(`No evaluator found for condition type: ${condition.type}`);
      return () => {};
    }

    const dispose = evaluator.watch(condition, callback);
    this.disposers.add(dispose);

    return () => {
      dispose();
      this.disposers.delete(dispose);
    };
  }

  private _watchAnd(conditions: ParsedCondition[], callback: () => void): () => void {
    let disposed = false;
    const activeDisposers: (() => void)[] = [];

    const watchNext = (index: number) => {
      if (disposed) return;
      if (index >= conditions.length) {
        callback();
        return;
      }

      const dispose = this._watchSingle(safe(conditions[index]), () => {
        watchNext(index + 1);
      });
      activeDisposers.push(dispose);
    };

    watchNext(0);

    return () => {
      disposed = true;
      for (const d of activeDisposers) d();
    };
  }

  private _watchOr(conditions: ParsedCondition[], callback: () => void): () => void {
    let matched = false;
    const activeDisposers: (() => void)[] = [];

    for (const condition of conditions) {
      const dispose = this._watchSingle(condition, () => {
        if (matched) return;
        matched = true;
        for (const d of activeDisposers) d();
        callback();
      });
      activeDisposers.push(dispose);
    }

    return () => {
      matched = true;
      for (const d of activeDisposers) d();
    };
  }

  dispose(): void {
    this.disposers.forEach((dispose) => {
      dispose();
    });
    this.disposers.clear();
    this.evaluators.forEach((evaluator) => {
      evaluator.dispose();
    });
  }
}
